/**
 * Kaelmoor.io client bootstrap.
 *
 * Layer order (all inside one scaled world container):
 *   terrain texture -> territory overlay -> structures -> units -> particles
 *   -> overlay graphics (bars, selection, pings) -> name labels
 */

import { Application, Container, Sprite } from 'pixi.js';
import { ART, Fx, MAP_H, MAP_W, Phase } from '../shared/constants';
import { generateMap } from '../shared/mapgen';
import * as ads from './ads';
import { ANCHOR_REFRESH_MS, refreshAnchors, stepAnchors } from './anchors';
import { Camera } from './camera';
import { markDevice } from './platform';
import * as portal from './crazygames';
import { SPAWN_ZOOM, keys, setupInput } from './input';
import { net } from './net';
import { EntityLayer } from './render/entities';
import { Particles } from './render/particles';
import { buildSprites, faviconUrl } from './render/sprites';
import { buildTerrain } from './render/terrain';
import { TerritoryLayer } from './render/territory';
import { WallLayer } from './render/walls';
import { sfx } from './sound';
import { syncViewport, trackViewport } from './viewport';
import type { NetPlayer } from './state';
import { state } from './state';
import * as ui from './ui';

async function main() {
  const canvas = document.getElementById('game') as HTMLCanvasElement;
  const app = new Application();
  await app.init({
    canvas,
    background: '#05070d',
    antialias: false,
    powerPreference: 'high-performance',
  });

  /*
   * **The renderer is told the canvas's real size, not the window's.**
   *
   * `resizeTo: window` used to do this and was wrong on every phone: it sizes
   * the drawing buffer from `window.innerHeight` while the stylesheet sizes the
   * element itself, and on mobile those two are different numbers whenever the
   * address bar is on screen. See `client/viewport.ts` — the whole reason taps
   * landed somewhere other than the fingertip.
   */
  trackViewport(canvas, (w, h) => app.renderer.resize(w, h));

  buildSprites();

  /*
   * The tab icon, once the art tables are rasterised. It is set from here
   * rather than written into index.html because it *is* the Knight sprite —
   * drawing it any other way would mean maintaining a second picture of the
   * same thing, which is the drift this project keeps designing out.
   */
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    ?? document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'icon' }));
  const drawn = faviconUrl();
  if (drawn) icon.href = drawn;

  const world = new Container();
  world.eventMode = 'none';
  app.stage.addChild(world);

  const terrainHolder = new Container();
  const territory = new TerritoryLayer();
  const walls = new WallLayer();
  const entities = new EntityLayer();
  const particles = new Particles();
  world.addChild(
    terrainHolder,
    territory.sprite,
    // walls sit on the ground, over the ownership paint and under everything
    // that walks — including the bastions built into them
    walls.graphics,
    entities.structures,
    entities.units,
    particles.container,
    entities.overlay,
    entities.labels,
  );

  const camera = new Camera();

  /*
   * The portal SDK, before the menu is wired up — the name field wants the
   * signed-in username as its default, and an invite link has to be read before
   * the room field is filled in. Bounded to three seconds and it never rejects,
   * so a blocked or slow CDN costs a short wait and nothing else. Compiles away
   * entirely in every build that is not `--mode crazygames`.
   */
  await portal.initPlatform();
  // the funnel's first step, for a menu the game server did not serve
  portal.countArrival();
  // finger or mouse — stamps the document so the stylesheet can lay itself out
  // for a thumb, and takes the build palette out on a touch screen
  markDevice();

  ui.initUI((name, room, create, opts) => {
    document.getElementById('menu-status')!.textContent = 'Connecting…';
    // the first click is also our licence to make noise
    sfx.unlock();
    sfx.startMusic();
    net.connect(name, create ? undefined : room || undefined, create, opts);
  });
  // any click anywhere is enough for the browser's autoplay policy
  window.addEventListener('pointerdown', () => sfx.unlock(), { once: true });
  ui.hooks.cameraTile = () => ({ x: camera.x / ART, y: camera.y / ART });
  // "look at their lands" travels there, so you can see whose ground you cross
  ui.hooks.focusTile = (x, y, zoom) => {
    camera.userControlled = false;
    camera.glideTo(x, y, zoom);
    camera.userControlled = true;
  };

  setupInput(canvas, camera);

  /* ------------------------------------------------------- net handlers */

  // the world is painted in 2048-pixel chunks, so it never asks a GPU for a
  // texture larger than WebGL guarantees — see CHUNK_TILES in render/terrain.ts
  let terrainSprites: Sprite[] = [];

  const buildWorld = () => {
    // byte-identical to the server's, from the same seed and the same shape
    const map = generateMap(state.seed, state.worldW, state.worldH);
    state.terrain = map.terrain;
    state.river = map.river;
    state.landTiles = map.landTiles;
    const { chunks } = buildTerrain(map.terrain, state.seed, state.worldW, state.worldH);
    terrainHolder.removeChildren();
    for (const s of terrainSprites) s.destroy();
    terrainSprites = chunks.map((c) => {
      const s = new Sprite(c.texture);
      s.position.set(c.x, c.y);
      terrainHolder.addChild(s);
      return s;
    });
    territory.refreshColors();
    state.fullRedraw = true;
  };

  /**
   * The camera's single trip to your homeland, taken as the war opens.
   *
   * False means one is still owed. It is set when the flight actually happens —
   * not when the phase changes — because the two are not the same moment: see
   * the frame loop below.
   */
  let flewHome = false;

  net.handlers = {
    onOpen: () => {
      document.getElementById('menu-status')!.textContent = 'Connected — waiting for the war council…';
    },
    onClose: () => {
      ui.showScreen('menu');
      ui.startRoomBrowser();
      document.getElementById('menu-status')!.textContent = 'Disconnected — pick a world to rejoin.';
    },
    onLobby: () => {
      if (state.phase === Phase.Lobby) {
        ui.showScreen('lobby');
        ui.updateLobby();
      }
    },
    onMatch: () => {
      buildWorld();
      ui.showScreen('game');
      camera.setWorld(state.worldW, state.worldH);
      // the whole continent at once: you cannot choose a coastline you cannot see
      camera.fitWorld();
      /*
       * A new world owes you a trip home — and this is the *only* place that
       * decides so. `match` is what a fresh match and a reconnect into a war
       * already running both arrive as, which is exactly the pair of cases that
       * want the flight; resetting it from the spawn-phase hint below instead
       * meant a rejoin never asked for one, because a live war has no spawn
       * phase left to pass through.
       */
      flewHome = false;
      ui.setHint(state.phase === Phase.Spawn ? 'Click a land tile to found your kingdom' : '');
    },
    onPhase: (phase) => {
      if (phase === Phase.Live) ui.setHint('');
      if (phase === Phase.Lobby) { ui.showScreen('lobby'); ui.updateLobby(); }
    },
    onNotice: (text, kind) => { ui.notice(text, kind); sfx.play('notice'); },
    onPing: () => sfx.play('ping'),
    onGameOver: (msg) => ui.showResults(msg),
    onEffect: (type, x, y, x2, y2, param) => {
      const tile = (Math.max(0, Math.min(MAP_H - 1, y | 0)) * MAP_W) + Math.max(0, Math.min(MAP_W - 1, x | 0));
      const owner = state.players.get(state.owner[tile]);
      particles.effect(type, x, y, x2, y2, param, owner ? owner.color : 0xffffff);
      if (type === Fx.Death) sfx.play('death');
      else if (type === Fx.Arrow) sfx.play('arrow');
      else if (type === Fx.Hit || type === Fx.Fireball) sfx.play('attack');
      else if (type === Fx.Meteor) sfx.play('meteor');
      else if (type === Fx.Build || type === Fx.Upgrade) sfx.play('build');
      else if (type === Fx.Trade || type === Fx.Loot) sfx.play('coin');
      else if (type === Fx.Spawn) sfx.play('train');
      else if (type === Fx.Capture) sfx.play('capture');
      else if (type === Fx.Clash) sfx.play('attack');
      else if (type === Fx.Wrath) sfx.play('meteor');
    },
  };

  // the slots have to exist before the first screen goes up, so that showing
  // the menu can fill them in the same breath (web build only — this is a
  // no-op that compiles away entirely under `npm run build:steam`)
  ads.initAds();

  ui.showScreen('menu');
  document.getElementById('menu-status')!.textContent = 'Pick a world to join, or start a new one.';

  // dev-only debug handle: window.__kaelmoor.state / .camera / .net / .ui
  if (import.meta.env?.DEV) {
    (window as any).__kaelmoor = { state, camera, net, app, particles, ui, ads, entities };
  }

  /* ------------------------------------------------------------- frame */

  let lastHud = 0;
  let lastView = 0;
  let lastAnchors = 0;
  let lastTime = performance.now();

  app.ticker.add(() => {
    const now = performance.now();
    const dt = Math.min(0.05, (now - lastTime) / 1000);
    lastTime = now;

    // an address bar that slid away without saying so leaves the world drawn
    // into a box that is no longer the box it is stretched across
    syncViewport(now);
    camera.setScreen(app.screen.width, app.screen.height);

    // keyboard panning
    let kx = 0, ky = 0;
    if (keys.has('a') || keys.has('arrowleft')) kx += 1;
    if (keys.has('d') || keys.has('arrowright')) kx -= 1;
    if (keys.has('w') || keys.has('arrowup')) ky += 1;
    if (keys.has('s') || keys.has('arrowdown')) ky -= 1;
    if (kx || ky) camera.panScreen(kx * 950 * dt, ky * 950 * dt);
    camera.step(dt);
    camera.apply(world);

    territory.update();
    walls.update();
    /*
     * Where each kingdom's name belongs, and the glide toward it. The sweep is
     * the expensive half and runs on its own clock; the ease runs every frame,
     * because a name that jumps once a second reads as a name that flickers.
     */
    if (now - lastAnchors > ANCHOR_REFRESH_MS) { lastAnchors = now; refreshAnchors(); }
    stepAnchors(dt);
    entities.update(now, camera.zoom);
    particles.update(dt);

    // the HUD is DOM-heavy; 5 Hz is indistinguishable from every frame
    if (now - lastHud > 200) {
      lastHud = now;
      if (state.phase === Phase.Lobby) ui.updateLobby();
      else ui.updateHUD();

      const me = state.players.get(state.you);
      if (state.phase === Phase.Spawn && me) {
        const left = Math.max(0, Math.ceil(state.countdown));
        ui.setHint(me.count === 0
          ? `Choose your homeland — ${left}s\nclick any land tile`
          : `Click anywhere else to move — ${left}s\nyou can see where everyone else is settling`);
      }
      /*
       * The one flight, at the one moment it belongs.
       *
       * Nothing moves the camera while people are still choosing — that phase is
       * for looking around, and a view that keeps swooping back to your own
       * circle is a view that hides your neighbours. Once the war is on, the map
       * travels to wherever you actually ended up (the server may have nudged
       * your site, and an auto-placed kingdom has to be found at all).
       *
       * **It waits for the ground, not for the phase.** "The war has started"
       * and "here is who owns what" are two separate messages, and on a
       * reconnect into a war already running the second one lands a beat after
       * the first. Flying the moment the phase flips would therefore aim at a
       * kingdom of nought tiles — which is 0,0, or the middle of the map once
       * the clamp has had it. So `homeTile` answers null until somebody can
       * actually say where home is, and this asks again 200 ms later.
       */
      if (state.phase === Phase.Live && !flewHome) {
        const home = homeTile(me);
        if (home) {
          flewHome = true;
          camera.glideTo(home.x, home.y, SPAWN_ZOOM);
        } else if (state.you === 0 || state.spectating || me?.eliminated || state.defeated) {
          /*
           * There is no home to go to, and there is not going to be one.
           * Somebody watching a world they were never dealt a kingdom in, and
           * somebody whose kingdom has fallen, are both here for the war rather
           * than for a province of it — so they keep the whole map, and stop
           * being asked about it.
           *
           * **A missing `me` is not one of those cases**, and reading it as one
           * was the bug: `welcome` hands over your kingdom's id, but your row in
           * the roster only lands with the first snapshot a beat later, so for
           * the first tick or two of every match `players.get(you)` is empty.
           * Treating that as "no kingdom" burned the one flight home on a
           * `fitWorld()` before the player had a kingdom to fly to, and the
           * camera then sat on the middle of the map for the whole war. The
           * honest test is the id the server gave us: 0 is a spectator, and
           * anything else is a king whose ground we have not been told about
           * yet — so we wait, and ask again in 200 ms.
           */
          flewHome = true;
          if (!camera.userControlled) camera.fitWorld();
        }
      }
    }

    if (now - lastView > 400) {
      lastView = now;
      const r = camera.viewRect();
      net.send({ t: 'view', x0: r.x0, y0: r.y0, x1: r.x1, y1: r.y1, ids: visibleRealms(r) });
      net.heartbeat();
    }

    // the lobby/spawn clocks count down; the war clock counts up
    if (state.phase === Phase.Live) state.countdown += dt;
    else if (state.countdown > 0) state.countdown = Math.max(0, state.countdown - dt);
  });
}

/**
 * Where your kingdom is, in tiles — or null while nobody can say yet.
 *
 * Two sources, in the order they can be trusted to be *your* ground. The
 * ownership map this client has decoded is the honest one, because it is the
 * same paint the player is looking at. Until it arrives there is the centroid
 * the server computes for you, which comes down with every snapshot and is what
 * the race power's reach is already measured from; it is cleared with the rest
 * of the world (`resetWorld`), so it can never be a leftover from the last war.
 *
 * Null is a real answer and not a failure: for a beat after a match opens
 * nobody holds anything as far as this client knows, and the caller is expected
 * to wait rather than to invent a destination out of two zeroes.
 */
function homeTile(me: NetPlayer | undefined): { x: number; y: number } | null {
  if (!me || me.eliminated) return null;
  if (me.count > 0) return { x: me.sumX / me.count, y: me.sumY / me.count };
  const { centroidX, centroidY } = state.self;
  if (centroidX > 0 && centroidY > 0) return { x: centroidX, y: centroidY };
  return null;
}

/**
 * Which kingdoms this screen is showing — the list the server encodes stats for.
 *
 * The client is the cheap place to work this out: the ownership map is already
 * here and already painted every frame, whereas the server would have to sweep
 * the viewport tile by tile for every connection, several times a second, on
 * the one thread the whole world runs on. Sampling every other tile keeps even
 * a fully zoomed-out screen under a millisecond; the only thing that can hide
 * in the gaps is a holding smaller than two tiles across, which has no label on
 * it to fill in anyway.
 *
 * Whoever is under the cursor is added outright, because pointing at a province
 * is the one way to ask about a kingdom deliberately.
 */
function visibleRealms(r: { x0: number; y0: number; x1: number; y1: number }): number[] {
  const ids = new Set<number>();
  const x0 = Math.max(0, Math.floor(r.x0)), x1 = Math.min(MAP_W - 1, Math.ceil(r.x1));
  const y0 = Math.max(0, Math.floor(r.y0)), y1 = Math.min(MAP_H - 1, Math.ceil(r.y1));
  for (let y = y0; y <= y1; y += 2) {
    const row = y * MAP_W;
    for (let x = x0; x <= x1; x += 2) {
      const o = state.owner[row + x];
      if (o !== 0) ids.add(o);
    }
  }
  if (state.hoverTile >= 0) {
    const o = state.owner[state.hoverTile];
    if (o !== 0) ids.add(o);
  }
  return [...ids];
}

main().catch((err) => {
  console.error(err);
  const status = document.getElementById('menu-status');
  if (status) status.textContent = 'Failed to start: ' + err.message;
});
